import { useState } from "react";
import { Check, Loader2, Pencil, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import { useOmieAtualizarCompetencia, useOmieCompetencias } from "@/hooks/use-finance-omie";
import type { OmieCompetencia, OmieCompetenciaBody, OmieEmpresaChave } from "@/types/finance";

const COMPETENCIA_REGEX = /^(0[1-9]|1[0-2])\.\d{4}$/;

const NOME_CURTO_EMPRESA: Partial<Record<OmieEmpresaChave, string>> = {
  corisco: "Corisco",
  faisca: "Faísca",
  gota_serena: "Gota Serena",
};

function competenciaParaNumero(competencia: string): number {
  const [mes, ano] = competencia.split(".").map(Number);
  return ano * 12 + (mes - 1);
}

function numeroParaCompetencia(numero: number): string {
  const ano = Math.floor(numero / 12);
  const mes = (numero % 12) + 1;
  return `${String(mes).padStart(2, "0")}.${ano}`;
}

/** Próximo mês sem linha depois da competência mais recente; sem nenhuma, o mês atual. */
function proximaCompetencia(existentes: string[]): string {
  const validas = existentes.filter((competencia) => COMPETENCIA_REGEX.test(competencia));
  const hoje = new Date();
  let numero =
    validas.length > 0
      ? Math.max(...validas.map(competenciaParaNumero)) + 1
      : hoje.getFullYear() * 12 + hoje.getMonth();
  while (validas.includes(numeroParaCompetencia(numero))) numero += 1;
  return numeroParaCompetencia(numero);
}

/** Tela de mapeamento competência ("MM.YYYY") -> código do projeto no Omie,
 * uma coluna por empresa ativa. Os códigos são cadastrados à mão, edição inline. */
export function CompetenciasTab() {
  const { toast } = useToast();
  const competenciasQuery = useOmieCompetencias();
  const atualizar = useOmieAtualizarCompetencia();
  const [competenciaEmEdicao, setCompetenciaEmEdicao] = useState<string | null>(null);
  const [novaCompetencia, setNovaCompetencia] = useState<string | null>(null);
  const [codigosEditados, setCodigosEditados] = useState<Partial<Record<OmieEmpresaChave, string>>>({});

  const empresas = competenciasQuery.data?.empresas ?? [];
  const itens = competenciasQuery.data?.items ?? [];

  const iniciarEdicao = (item: OmieCompetencia) => {
    setNovaCompetencia(null);
    setCompetenciaEmEdicao(item.competencia);
    setCodigosEditados(
      Object.fromEntries(
        empresas.map((empresa) => {
          const codigo = item.codigos[empresa.chave];
          return [empresa.chave, codigo != null ? String(codigo) : ""];
        }),
      ),
    );
  };

  const iniciarNovoMes = () => {
    setCompetenciaEmEdicao(null);
    setNovaCompetencia(proximaCompetencia(itens.map((item) => item.competencia)));
    setCodigosEditados({});
  };

  const cancelarEdicao = () => {
    setCompetenciaEmEdicao(null);
    setNovaCompetencia(null);
  };

  const salvar = async (competencia: string, novo: boolean) => {
    if (!COMPETENCIA_REGEX.test(competencia)) {
      toast({ variant: "destructive", title: "Use o formato MM.AAAA, por exemplo 10.2026" });
      return;
    }
    if (novo && itens.some((item) => item.competencia === competencia)) {
      toast({
        variant: "destructive",
        title: "Essa competência já existe",
        description: "Edite a linha dela na tabela.",
      });
      return;
    }
    const codigos: OmieCompetenciaBody["codigos"] = {};
    for (const empresa of empresas) {
      const valor = (codigosEditados[empresa.chave] ?? "").trim();
      if (valor !== "") codigos[empresa.chave] = Number(valor);
    }
    if (Object.keys(codigos).length === 0) {
      toast({ variant: "destructive", title: "Informe ao menos um código de projeto" });
      return;
    }
    try {
      await atualizar.mutateAsync({ competencia, body: { codigos } });
      toast({ title: novo ? "Competência adicionada" : "Competência atualizada" });
      cancelarEdicao();
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  if (competenciasQuery.isLoading) return <LoadingState variant="list" count={4} />;
  if (competenciasQuery.isError) {
    return <ErrorState onRetry={() => competenciasQuery.refetch()} />;
  }

  const camposDeCodigo = () =>
    empresas.map((empresa) => (
      <TableCell key={empresa.chave}>
        <Input
          type="number"
          value={codigosEditados[empresa.chave] ?? ""}
          onChange={(e) =>
            setCodigosEditados((atuais) => ({ ...atuais, [empresa.chave]: e.target.value }))
          }
          className="h-8 w-36"
        />
      </TableCell>
    ));

  const acoesDeEdicao = (competencia: string, novo: boolean) => (
    <div className="flex justify-end gap-1">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        disabled={atualizar.isPending}
        onClick={() => salvar(competencia, novo)}
      >
        {atualizar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </Button>
      <Button type="button" size="icon" variant="ghost" onClick={cancelarEdicao}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={iniciarNovoMes} disabled={novaCompetencia !== null} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Adicionar mês
        </Button>
      </div>

      {itens.length === 0 && novaCompetencia === null ? (
        <EmptyState
          title="Nenhuma competência cadastrada"
          description="Cadastre à mão, mês a mês, o código do projeto de cada empresa no Omie."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Competência</TableHead>
                {empresas.map((empresa) => (
                  <TableHead key={empresa.chave}>
                    Código {NOME_CURTO_EMPRESA[empresa.chave] ?? empresa.nome}
                  </TableHead>
                ))}
                <TableHead className="text-right">Editar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {novaCompetencia !== null && (
                <TableRow>
                  <TableCell>
                    <Input
                      value={novaCompetencia}
                      onChange={(e) => setNovaCompetencia(e.target.value)}
                      placeholder="MM.AAAA"
                      className="h-8 w-28"
                    />
                  </TableCell>
                  {camposDeCodigo()}
                  <TableCell className="text-right">{acoesDeEdicao(novaCompetencia.trim(), true)}</TableCell>
                </TableRow>
              )}
              {itens.map((item) => {
                const editando = competenciaEmEdicao === item.competencia;
                return (
                  <TableRow key={item.competencia}>
                    <TableCell className="font-medium">{item.competencia}</TableCell>
                    {editando
                      ? camposDeCodigo()
                      : empresas.map((empresa) => (
                          <TableCell key={empresa.chave}>{item.codigos[empresa.chave] ?? "-"}</TableCell>
                        ))}
                    <TableCell className="text-right">
                      {editando ? (
                        acoesDeEdicao(item.competencia, false)
                      ) : (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => iniciarEdicao(item)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
